import { useCallback, useEffect, useRef } from 'react'
import type { RefObject } from 'react'

type SwipeDirection = 'down' | 'left' | 'right' | 'up'

interface UseOnSwipeParams {
  direction: SwipeDirection
  onSwipe: () => void
  targetRef?: RefObject<HTMLElement | null>
}

interface Touch {
  startX: number
  startY: number
}

export function useOnSwipe({ direction, onSwipe, targetRef }: UseOnSwipeParams): void {
  const touchRef = useRef<Touch>()
  const onSwipeRef = useRef(onSwipe)
  onSwipeRef.current = onSwipe

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!e.changedTouches?.length) return
    if (!touchRef.current) touchRef.current = {} as Touch
    touchRef.current.startX = e.changedTouches[0].clientX
    touchRef.current.startY = e.changedTouches[0].clientY
  }, [])

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchRef.current) return
      if (!e.changedTouches?.length) return

      switch (direction) {
        case 'left': {
          return touchRef.current.startX < e.changedTouches[0].clientX && onSwipeRef.current()
        }
        case 'right': {
          return touchRef.current.startX > e.changedTouches[0].clientX && onSwipeRef.current()
        }
        case 'up': {
          return touchRef.current.startY < e.changedTouches[0].clientY && onSwipeRef.current()
        }
        case 'down': {
          return touchRef.current.startY > e.changedTouches[0].clientY && onSwipeRef.current()
        }
      }
    },
    [direction],
  )

  useEffect(() => {
    const target = targetRef?.current ?? document.documentElement
    target.addEventListener('touchstart', handleTouchStart)
    target.addEventListener('touchend', handleTouchEnd)

    return (): void => {
      target.removeEventListener('touchstart', handleTouchStart)
      target.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchEnd, handleTouchStart, targetRef])
}
